import React from 'react';
import { MessageCircle } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { useChatStore } from '@/stores/chatStore';

/**
 * Floating chat button — refined-minimal variant, unified across the 3 apps.
 *
 * 44×44 brand-primary circle with an 18×18 icon. Static at rest. On
 * HOVER the button gently lifts (scale + shadow + icon tilt); on press
 * it dips. A 150ms opacity fade on mount/unmount keeps the swap with
 * the chat panel smooth. `useReducedMotion()` collapses every animation
 * to instant for users with the OS-level preference.
 */
export const ChatButton: React.FC = () => {
  const toggleWidget = useChatStore((s) => s.toggleWidget);
  const unreadCount = useChatStore((s) => s.unreadCount);
  const reduceMotion = useReducedMotion();

  return (
    <motion.button
      type="button"
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      whileHover={reduceMotion ? undefined : { scale: 1.05 }}
      whileTap={reduceMotion ? undefined : { scale: 0.95 }}
      transition={{ duration: reduceMotion ? 0 : 0.15 }}
      onClick={toggleWidget}
      aria-label="Open live chat"
      className="group relative flex h-[44px] w-[44px] items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-[background-color,box-shadow] duration-200 hover:bg-primary/90 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      <MessageCircle
        className="h-[18px] w-[18px] transition-transform duration-200 motion-safe:group-hover:rotate-12"
        aria-hidden="true"
      />

      {unreadCount > 0 && (
        <span
          className="absolute -right-0.5 -top-0.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground"
          aria-label={`${unreadCount} unread ${unreadCount === 1 ? 'message' : 'messages'}`}
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}

      <span
        role="tooltip"
        className="pointer-events-none absolute right-full top-1/2 mr-[10px] -translate-y-1/2 whitespace-nowrap rounded-md bg-[#020817] px-[8px] py-[5px] text-[11px] font-medium leading-[14px] text-white opacity-0 shadow-sm transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
      >
        Live chat
      </span>
    </motion.button>
  );
};
